import React, { useCallback, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';

import {
  CARD_PADDING,
  COEFFICIENT_DIGITAL_HEIGHT,
  DEFAULT_ANIMATION_TIME,
  INITIAL_OFFSET,
} from 'components/widgets/Rail/constants';
import { animate } from 'components/widgets/Rail/utils';
import { NEXT_BUTTON_WIDTH } from 'consts';

interface Props {
  setRailCardsListRefScrollTo: (value: number) => void;
  items: any[];
  totalWidth: number;
  offset: React.MutableRefObject<any> | undefined;
  railCardsListRef: React.MutableRefObject<List | null>;
  refElementInner: React.MutableRefObject<HTMLDivElement | null>;
  imageWidth: number;
  scrollHandle: (newOffset: number, blockWidth: number) => void;
  imageHeight: number;
  hasAssetsMarkedByNumber?: boolean;
}

export const useArrowsClickHook = ({
  scrollHandle,
  setRailCardsListRefScrollTo,
  items,
  totalWidth,
  offset,
  railCardsListRef,
  refElementInner,
  imageWidth,
  imageHeight,
  hasAssetsMarkedByNumber,
}: Props) => {
  const isAnimationActive = useRef(false);
  const setIsAnimation = useCallback((value: boolean) => {
    isAnimationActive.current = value;
  }, []);

  return useCallback(
    (increase: boolean) => {
      if (isAnimationActive.current) {
        return;
      }

      const blockWidth = refElementInner?.current?.clientWidth || document.body.clientWidth;
      let newOffset = 0; // start rail from full card when navigating
      let totalAssetsWidth = 0;

      for (let i = 0; i < items.length; i += 1) {
        // hasAssetsMarkedByNumber an additional variable for recalculating the width of the card (top 10 rail card)
        const width = hasAssetsMarkedByNumber ? imageWidth + (imageHeight / COEFFICIENT_DIGITAL_HEIGHT) + CARD_PADDING : imageWidth + CARD_PADDING;
        // i element overlow to right
        if (increase && totalAssetsWidth + width + INITIAL_OFFSET > offset?.current + blockWidth) {

          newOffset = totalAssetsWidth - CARD_PADDING;

        } else if (!increase && totalAssetsWidth + width > totalWidth - offset?.current) {
          newOffset = totalWidth - totalAssetsWidth - blockWidth + NEXT_BUTTON_WIDTH;
          // in some situations small part of space may stay, and we will have left arrow button
          if (newOffset <= NEXT_BUTTON_WIDTH) {
            newOffset = 0;
          }
        } else {
          totalAssetsWidth += width;
        }
      }

      if (newOffset < 0) {
        newOffset = 0;
      }

      let delta = newOffset - offset?.current;

      if (newOffset + blockWidth > totalWidth) {
        newOffset = totalWidth - blockWidth;
        delta = totalWidth - offset?.current - blockWidth;
      }

      const animationTime = (Math.abs(delta) / blockWidth) * DEFAULT_ANIMATION_TIME;

      animate(setIsAnimation, railCardsListRef, delta, animationTime, () => {
        scrollHandle(newOffset, blockWidth);
        setRailCardsListRefScrollTo(newOffset);
      });
    },
    [isAnimationActive.current, totalWidth, items.length, offset?.current],
  );
};
